import isEmpty from 'lodash/isEmpty';

import { MODULE_NAME } from './const';
import { useAnalyticsConfigurations, useAnalyticsProviders } from './getters';
import {
	SET_ANALYTICS_PROVIDERS,
	SET_ANALYTICS_CONFIGURATIONS,
	SET_ANALYTICS_CONFIGURATION,
	DELETE_ANALYTICS_CONFIGURATION,
} from './mutations';
import StoreModuleService from '../StoreModuleService';
import { selectedProjectId } from '../selectedProject/getters';
import api from '@/api';
import { arrayToObject } from '@/utils/helpers';
import { VIRTUAL_PROJECT_IDS } from '@/utils/projects.const';

const buildActionObject = (action) => StoreModuleService.buildActionObject(MODULE_NAME, action);

// actions declarations
export const fetchAnalyticsProviders = buildActionObject('fetchAnalyticsProviders');
export const setAnalyticsProviders = buildActionObject('setAnalyticsProviders');
export const fetchAnalyticsConfigurations = buildActionObject('fetchAnalyticsConfigurations');
export const setAnalyticsConfigurations = buildActionObject('setAnalyticsConfigurations');
export const addAnalyticsConfiguration = buildActionObject('addAnalyticsConfiguration');
export const setAnalyticsConfiguration = buildActionObject('setAnalyticsConfiguration');
export const deleteAnalyticsConfiguration = buildActionObject('deleteAnalyticsConfiguration');
export const removeAnalyticsConfiguration = buildActionObject('removeAnalyticsConfiguration');
export const updateAnalyticsConfiguration = buildActionObject('updateAnalyticsConfiguration');
export const editAnalyticsEvent = buildActionObject('editAnalyticsEvent');
export const addAnalyticsEvent = buildActionObject('addAnalyticsEvent');
export const deleteAnalyticsEvent = buildActionObject('deleteAnalyticsEvent');

const actions = {
	...StoreModuleService.createDefaultActions(),
	async [fetchAnalyticsProviders.action]({ dispatch, getters }) {
		const analyticsProvidersInStore = getters[useAnalyticsProviders.getter];
		if (isEmpty(analyticsProvidersInStore)) {
			const analyticsProviders = await api.fetchAnalyticsProviders();
			const analyticsProvidersObj = arrayToObject(analyticsProviders);
			await dispatch(setAnalyticsProviders.action, { analyticsProviders: analyticsProvidersObj });
		}
	},
	[setAnalyticsProviders.action]({ commit }, { analyticsProviders }) {
		commit(SET_ANALYTICS_PROVIDERS, { analyticsProviders });
	},
	async [fetchAnalyticsConfigurations.action]({ dispatch, rootGetters }) {
		const projectId = VIRTUAL_PROJECT_IDS.includes(rootGetters[selectedProjectId.fullPath]) ? '' : rootGetters[selectedProjectId.fullPath];
		const analyticsConfigurations = await api.fetchAnalyticsConfigurations(projectId);
		const analyticsConfigurationsObj = arrayToObject(analyticsConfigurations);
		await dispatch(setAnalyticsConfigurations.action, { analyticsConfigurations: analyticsConfigurationsObj });
	},
	[setAnalyticsConfigurations.action]({ commit }, { analyticsConfigurations }) {
		commit(SET_ANALYTICS_CONFIGURATIONS, { analyticsConfigurations });
	},
	async [addAnalyticsConfiguration.action]({ dispatch }, { analyticsConfiguration }) {
		const newConfiguration = await api.addAnalyticsConfiguration(analyticsConfiguration);
		await dispatch(setAnalyticsConfiguration.action, { analyticsConfiguration: newConfiguration });
		return newConfiguration;
	},
	[setAnalyticsConfiguration.action]({ commit }, { analyticsConfiguration }) {
		commit(SET_ANALYTICS_CONFIGURATION, { analyticsConfiguration });
	},
	async [updateAnalyticsConfiguration.action]({ dispatch }, { analyticsConfiguration, id }) {
		const newConfiguration = await api.updateAnalyticsConfiguration(analyticsConfiguration, id);
		await dispatch(setAnalyticsConfiguration.action, { analyticsConfiguration: newConfiguration });
		return newConfiguration;
	},
	async [deleteAnalyticsConfiguration.action]({ dispatch }, { analyticsConfigurationId }) {
		await api.deleteAnalyticsConfiguration(analyticsConfigurationId);
		await dispatch(removeAnalyticsConfiguration.action, { analyticsConfigurationId });
	},

	[removeAnalyticsConfiguration.action]({ commit }, { analyticsConfigurationId }) {
		commit(DELETE_ANALYTICS_CONFIGURATION, { analyticsConfigurationId });
	},

	async [addAnalyticsEvent.action]({ dispatch, getters }, { analyticsEvent, analyticsConfiguration }) {
		const newEvent = await api.addAnalyticsEvent({ ...analyticsEvent, analytics_provider_configuration: analyticsConfiguration.id });
		const analyticsConfigurationsInStore = getters[useAnalyticsConfigurations.getter];
		const analyticsConfigurationToUpdate = analyticsConfigurationsInStore.find((config) => config.id === analyticsConfiguration.id);
		analyticsConfigurationToUpdate.events.push(newEvent);
		await dispatch(setAnalyticsConfiguration.action, {
			analyticsConfiguration: { analyticsConfigurationToUpdate },
		});
	},
	async [editAnalyticsEvent.action]({ dispatch }, { analyticsEvent, id, analyticsConfiguration }) {
		await api.updateAnalyticsEvent(analyticsEvent, id);
		await dispatch(setAnalyticsConfiguration.action, {
			analyticsConfiguration,
		});
	},
	async [deleteAnalyticsEvent.action]({ dispatch, getters }, { analyticsEventId, analyticsConfiguration }) {
		await api.deleteAnalyticsEvent(analyticsEventId);
		const analyticsConfigurationsInStore = getters[useAnalyticsConfigurations.getter];
		const analyticsConfigurationToUpdate = analyticsConfigurationsInStore.find((config) => config.id === analyticsConfiguration.id);
		const newEvents = analyticsConfiguration.events.filter((event) => event.id !== analyticsEventId);
		analyticsConfigurationToUpdate.events = newEvents;
		await dispatch(setAnalyticsConfiguration.action, {
			analyticsConfiguration: analyticsConfigurationToUpdate,
		});
	},
};

export default actions;
